"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, CircleCheck as CheckCircle2, Download, FileText, Layers, Loader, ShieldCheck, Sparkles } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface ChapterItem {
  id: string;
  number: string;
  title: string;
  summary: string;
  pages: string;
}
const chapters: ChapterItem[] = [{
  id: "item-1",
  number: "01",
  title: "Foundation & Cross-Functional Alignment",
  summary: "Aligning revenue operations, marketing pipelines, and customer lifecycle milestones under one unified measurement model.",
  pages: "pp. 08–22"
}, {
  id: "item-2",
  number: "02",
  title: "Data Integrity & Attribution Architectures",
  summary: "Building resilient first-party data capture routines, eliminating pipeline blind spots, and validating multi-touch attribution.",
  pages: "pp. 23–41"
}, {
  id: "item-3",
  number: "03",
  title: "Execution Playbooks & Scaling Thresholds",
  summary: "Practical stage-gate criteria for team expansions, tech stack rationalization, and predictable quarter-over-quarter forecasting.",
  pages: "pp. 42–64"
}];
const highlights = ["64-page peer-reviewed tactical framework", "Complete data model audit checklists", "Unrestricted PDF and EPUB delivery", "Zero sponsored bias or vendor promotions"];
function EbookForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-md border border-border bg-muted/40 p-6 text-center">
        <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h4 className="font-serif text-lg font-semibold text-foreground">
          Your download is ready
        </h4>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage || "We have sent the PDF and supplementary frameworks directly to your inbox."}
        </p>
        <Button onClick={resetForm} variant="outline" size="sm" className="mt-5 rounded-sm border-border text-foreground hover:bg-muted">
          Request another copy
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Full Name <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="Alex Smith" className="h-10 rounded-sm border-input bg-card px-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="business_email" rules={{
      required: "Business email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Business Email <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="alex@company.com" className="h-10 rounded-sm border-input bg-card px-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="job_title" render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Job Title
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="VP of Marketing" className="h-10 rounded-sm border-input bg-card px-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-sm border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-sm bg-primary py-3 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:opacity-50">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Preparing document...
          </> : <>
            <Download className="mr-2 size-4" />
            Get Instant PDF Access
          </>}
      </Button>

      <div className="flex items-center justify-center gap-1.5 pt-1 text-center text-xs text-muted-foreground">
        <ShieldCheck className="size-3.5 text-muted-foreground" />
        <span>Strict privacy standards. No marketing spam.</span>
      </div>
    </Form>;
}
export default function Spotlight() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="featured-ebook-spotlight" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Section Masthead Header */}
        <div className="mb-12 border-b border-border pb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="inline-flex items-center gap-2">
              <span className="inline-block size-2 rounded-full bg-foreground" />
              <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Featured Publication
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-sm border border-border bg-card px-2.5 py-0.5 font-mono text-xs text-muted-foreground">
                EDITION 2025.Q2
              </span>
              <span className="rounded-sm border border-border bg-card px-2.5 py-0.5 font-mono text-xs text-muted-foreground">
                64 PAGES
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Left Column: Publication Cover & Meta */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex flex-col items-center lg:col-span-5 lg:items-start">
            {/* Book Representation Card */}
            <div className="group relative w-full max-w-[380px] rounded-md border border-border bg-card p-6 shadow-md transition-[box-shadow,transform] duration-250 hover:-translate-y-1 hover:shadow-lg">
              {/* Publication Spine Effect */}
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-sm border border-border bg-muted">
                {/* Physical bound spine crease gradient */}
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6" style={{
                background: "linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(255,255,255,0.2) 20%, rgba(0,0,0,0.06) 40%, transparent 100%)"
              }} />
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/revenue_architecture_guide_cover.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/revenue_architecture_guide_cover.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/revenue_architecture_guide_cover.webp-wvc-sizes" alt="The Modern Executive Guide to Revenue Architecture" loading="lazy" decoding="async" className="size-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-30" />
                <div className="absolute top-3 right-3 z-10 rounded-sm bg-background/90 px-2 py-1 font-mono text-[10px] font-semibold text-foreground backdrop-blur-xs">
                  EXECUTIVE BRIEF
                </div>
              </div>

              {/* Book Metadata Footer */}
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-mono">MINFOMEDIA RESEARCH</span>
                  <span className="font-mono">REF: MM-2025-EB4</span>
                </div>
                <div className="border-t border-border pt-3">
                  <h4 className="font-serif text-base font-semibold text-foreground">
                    The Modern Revenue Architecture
                  </h4>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Peer-reviewed methodologies for B2B commercial leadership.
                  </p>
                </div>
              </div>
            </div>

            {/* Highlights List */}
            <div className="mt-8 w-full max-w-[380px] space-y-2.5">
              <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Included in this publication
              </h4>
              <ul className="space-y-2 text-sm text-foreground">
                {highlights.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5 text-xs text-muted-foreground sm:text-sm">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-foreground" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
          </motion.div>

          {/* Right Column: Executive Summary, Outline & Download Form */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="flex flex-col lg:col-span-7">
            {/* Title & Category */}
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1">
                <Sparkles className="size-3 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">
                  Strategic Framework
                </span>
              </div>
              <h2 className="font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
                The modern executive guide to revenue architecture
              </h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">
                A structured blueprint designed for commercial leaders managing
                complex buyer journeys, high-velocity sales pipelines, and
                integrated commercial data systems.
              </p>
            </div>

            {/* Executive Summary Card */}
            <div className="mt-8 rounded-md border border-border bg-card p-6 shadow-xs">
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <BookOpen className="size-4 text-foreground" />
                <h3 className="font-serif text-base font-semibold text-foreground">
                  Executive Summary
                </h3>
              </div>
              <p className="mt-3.5 text-sm leading-relaxed text-muted-foreground">
                Traditional pipeline management models often isolate marketing,
                sales, and customer success data into disconnected functional
                silos. This guide presents an integrated architectural model
                that ties demand generation directly to customer lifetime
                value, offering clear diagnostic frameworks to identify and
                eliminate leakage points across the enterprise.
              </p>
            </div>

            {/* Chapter Outline Breakdown */}
            <div className="mt-8">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers className="size-4 text-foreground" />
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    Chapter Breakdown
                  </h3>
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  3 Core Sections
                </span>
              </div>

              <Accordion type="single" collapsible defaultValue="item-1" className="w-full rounded-md border border-border bg-card">
                {chapters.map((ch, i) => <AccordionItem key={ch.id} value={ch.id} data-index={i} className="border-b border-border px-5 last:border-b-0">
                    <AccordionTrigger className="py-4 hover:no-underline [&[data-state=open]>div>span.num]:text-foreground">
                      <div className="flex items-center gap-4 text-left">
                        <span className="num font-mono text-xs font-medium text-muted-foreground transition-colors">
                          {ch.number}
                        </span>
                        <div>
                          <p className="font-serif text-sm font-semibold text-foreground sm:text-base">
                            {ch.title}
                          </p>
                          <span className="font-mono text-[11px] text-muted-foreground">
                            {ch.pages}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-0 pb-4 pl-9 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {ch.summary}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {/* Inline Resource Access Form */}
            <div className="mt-10 rounded-md border border-border bg-card p-6 sm:p-8 shadow-xs">
              <div className="mb-6 flex flex-col gap-1 border-b border-border pb-4">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-foreground" />
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    Ebook Download Request
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground sm:text-sm">
                  Provide your work email below to receive the complete
                  publication and data appendices.
                </p>
              </div>

              <FormProvider formId="ebooks-guides_featured-ebook-spotlight_form" sectionName="featured-ebook-spotlight" defaultValues={{
              full_name: "",
              business_email: "",
              job_title: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              business_email: {
                required: true,
                pattern: "email"
              },
              job_title: {
                required: false
              }
            }} submitText="Get Instant PDF Access" successMessage="Your executive guide has been dispatched to your email address." errorMessage="Unable to complete your request. Please check your information and try again.">
                <EbookForm />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}